import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Users, MapPin, Award, Settings, Plus, Trash2, Upload, Image } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyAddress = {
  street: '',
  city: '',
  province: '',
  postal_code: '',
  country: 'South Africa'
};

const emptyContact = {
  contact_type: 'primary',
  name: '',
  email: '',
  phone: '',
  cell: '',
  position: ''
};

const emptyBranch = {
  name: '',
  is_head_office: false,
  physical_address: { ...emptyAddress },
  postal_address: { ...emptyAddress },
  billing_email: '',
  billing_contact: '',
  phone: '',
  fax: ''
};

const emptyCertification = {
  cidb_grade: null,
  cidb_certificate_number: '',
  cidb_expiry_date: '',
  cidb_certificate_url: '',
  bee_level: null,
  bee_certificate_number: '',
  bee_expiry_date: '',
  bee_certificate_url: ''
};

const contactTypes = [
  { value: 'primary', label: 'Primary Contact' },
  { value: 'accounting', label: 'Accounting Contact' },
  { value: 'director', label: 'Director' },
  { value: 'contracts', label: 'Contracts Contact' },
  { value: 'site', label: 'Site Contact' },
  { value: 'other', label: 'Other' }
];

const provinces = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'Northern Cape',
  'North West',
  'Western Cape'
];

// Extracted as standalone component to prevent re-render focus loss
const AddressFields = React.memo(({ address, onChange, prefix }) => (
  <div className="grid grid-cols-2 gap-3">
    <div className="col-span-2">
      <Label className="text-xs font-medium text-slate-500">Street Address</Label>
      <Input
        value={address?.street || ''}
        onChange={(e) => onChange(`${prefix}.street`, e.target.value)}
        placeholder="123 Main Street"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs font-medium text-slate-500">City</Label>
      <Input
        value={address?.city || ''}
        onChange={(e) => onChange(`${prefix}.city`, e.target.value)}
        placeholder="Johannesburg"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs font-medium text-slate-500">Province</Label>
      <Select
        value={address?.province || ''}
        onValueChange={(value) => onChange(`${prefix}.province`, value)}
      >
        <SelectTrigger className="mt-1">
          <SelectValue placeholder="Select province" />
        </SelectTrigger>
        <SelectContent>
          {provinces.map(p => (
            <SelectItem key={p} value={p}>{p}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs font-medium text-slate-500">Postal Code</Label>
      <Input
        value={address?.postal_code || ''}
        onChange={(e) => onChange(`${prefix}.postal_code`, e.target.value)}
        placeholder="2000"
        className="mt-1"
      />
    </div>
    <div>
      <Label className="text-xs font-medium text-slate-500">Country</Label>
      <Input
        value={address?.country || 'South Africa'}
        onChange={(e) => onChange(`${prefix}.country`, e.target.value)}
        className="mt-1"
      />
    </div>
  </div>
));

export default function ClientForm({ client, onSave, onCancel }) {
  const [activeTab, setActiveTab] = useState('company');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [form, setForm] = useState({
    // Company Details
    name: '',
    legal_name: '',
    registration_number: '',
    vat_number: '',
    website: '',
    logo_url: '',
    email: '',
    phone: '',
    
    // Addresses
    physical_address: { ...emptyAddress },
    postal_address: { ...emptyAddress },
    
    // Arrays
    branches: [],
    contacts: [{ ...emptyContact, contact_type: 'primary' }],
    
    // Certification
    certification: { ...emptyCertification },
    
    // Status
    is_active: true,
    deactivation_reason: '',
    notes: ''
  });

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || '',
        legal_name: client.legal_name || '',
        registration_number: client.registration_number || '',
        vat_number: client.vat_number || '',
        website: client.website || '',
        logo_url: client.logo_url || '',
        email: client.email || '',
        phone: client.phone || '',
        physical_address: client.physical_address || { ...emptyAddress },
        postal_address: client.postal_address || { ...emptyAddress },
        branches: client.branches || [],
        contacts: client.contacts?.length > 0 ? client.contacts : [{ ...emptyContact, contact_type: 'primary' }],
        certification: client.certification || { ...emptyCertification },
        is_active: client.is_active !== false,
        deactivation_reason: client.deactivation_reason || '',
        notes: client.notes || ''
      });
    }
  }, [client]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!client?.id) {
      toast.error('Please save the client first before uploading a logo');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.clients.uploadLogo(client.id, formData);
      setForm(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Company name is required');
      setActiveTab('company');
      return;
    }

    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  // Branch handlers
  const addBranch = () => {
    setForm(prev => ({
      ...prev,
      branches: [...prev.branches, { ...emptyBranch, id: Date.now().toString() }]
    }));
  };

  const updateBranch = (index, field, value) => {
    setForm(prev => {
      const branches = [...prev.branches];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        branches[index] = {
          ...branches[index],
          [parent]: { ...branches[index][parent], [child]: value }
        };
      } else {
        branches[index] = { ...branches[index], [field]: value };
      }
      return { ...prev, branches };
    });
  };

  const removeBranch = (index) => {
    setForm(prev => ({
      ...prev,
      branches: prev.branches.filter((_, i) => i !== index)
    }));
  };

  // Contact handlers
  const addContact = () => {
    setForm(prev => ({
      ...prev,
      contacts: [...prev.contacts, { ...emptyContact }]
    }));
  };

  const updateContact = (index, field, value) => {
    setForm(prev => {
      const contacts = [...prev.contacts];
      contacts[index] = { ...contacts[index], [field]: value };
      return { ...prev, contacts };
    });
  };

  const removeContact = (index) => {
    if (form.contacts.length === 1) {
      toast.error('At least one contact is required');
      return;
    }
    setForm(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
  };

  // Memoized address change handlers to prevent focus loss
  const handlePhysicalAddressChange = useCallback((field, value) => {
    const key = field.split('.')[1];
    setForm(prev => ({
      ...prev,
      physical_address: { ...prev.physical_address, [key]: value }
    }));
  }, []);

  const handlePostalAddressChange = useCallback((field, value) => {
    const key = field.split('.')[1];
    setForm(prev => ({
      ...prev,
      postal_address: { ...prev.postal_address, [key]: value }
    }));
  }, []);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="company" className="text-xs">
            <Building2 className="h-3 w-3 mr-1" />
            Company
          </TabsTrigger>
          <TabsTrigger value="branches" className="text-xs">
            <MapPin className="h-3 w-3 mr-1" />
            Branches
          </TabsTrigger>
          <TabsTrigger value="contacts" className="text-xs">
            <Users className="h-3 w-3 mr-1" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="certifications" className="text-xs">
            <Award className="h-3 w-3 mr-1" />
            Certifications
          </TabsTrigger>
          <TabsTrigger value="status" className="text-xs">
            <Settings className="h-3 w-3 mr-1" />
            Status
          </TabsTrigger>
        </TabsList>

        {/* Company Details Tab */}
        <TabsContent value="company" className="space-y-4 mt-4">
          {/* Logo Section */}
          <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-white overflow-hidden">
              {form.logo_url ? (
                <img 
                  src={form.logo_url.startsWith('/api/') ? `${BACKEND_URL}${form.logo_url}` : `${BACKEND_URL}/api${form.logo_url}`} 
                  alt="Company logo" 
                  className="w-full h-full object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Image className="h-8 w-8 text-slate-300" />
              )}
            </div>
            <div className="flex-1">
              <Label className="text-sm font-semibold">Company Logo</Label>
              <p className="text-xs text-slate-500 mb-2">
                Upload your client's logo. This will appear on all their documents and submissions.
              </p>
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('logo-upload').click()}
                disabled={uploading || !client?.id}
              >
                <Upload className="h-3 w-3 mr-1" />
                {uploading ? 'Uploading...' : 'Upload Logo'}
              </Button>
              {!client?.id && (
                <p className="text-xs text-amber-600 mt-1">Save client first to upload logo</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Trading Name <span className="text-red-500">*</span>
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Company trading name"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Legal Name</Label>
              <Input
                value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                placeholder="Registered company name"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Registration Number</Label>
              <Input
                value={form.registration_number}
                onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
                placeholder="e.g., 2020/123456/07"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">VAT Number</Label>
              <Input
                value={form.vat_number}
                onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                placeholder="e.g., 4123456789"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="info@company.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+27 11 123 4567"
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Website</Label>
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://www.company.co.za"
                className="mt-1"
              />
            </div>
          </div>

          {/* Main Address */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold mb-3">Physical Address</h4>
            <AddressFields
              address={form.physical_address}
              onChange={handlePhysicalAddressChange}
              prefix="physical_address"
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold mb-3">Postal Address</h4>
            <AddressFields
              address={form.postal_address}
              onChange={handlePostalAddressChange}
              prefix="postal_address"
            />
          </div>
        </TabsContent>

        {/* Branches Tab */}
        <TabsContent value="branches" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-sm font-semibold">Branch Offices</h4>
              <p className="text-xs text-slate-500">Add multiple branches for different billing addresses</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addBranch}>
              <Plus className="h-3 w-3 mr-1" />
              Add Branch
            </Button>
          </div>

          {form.branches.length === 0 ? (
            <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
              <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No branches added yet</p>
              <p className="text-xs">Click "Add Branch" to add office locations</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              {form.branches.map((branch, index) => (
                <div key={branch.id || index} className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={branch.name}
                        onChange={(e) => updateBranch(index, 'name', e.target.value)}
                        placeholder="Branch name (e.g., Durban Office)"
                        className="w-64"
                      />
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={branch.is_head_office}
                          onChange={(e) => updateBranch(index, 'is_head_office', e.target.checked)}
                          className="rounded"
                        />
                        Head Office
                      </label>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBranch(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <Label className="text-xs text-slate-500">Phone</Label>
                      <Input
                        value={branch.phone || ''}
                        onChange={(e) => updateBranch(index, 'phone', e.target.value)}
                        placeholder="+27 11 123 4567"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Fax</Label>
                      <Input
                        value={branch.fax || ''}
                        onChange={(e) => updateBranch(index, 'fax', e.target.value)}
                        placeholder="+27 11 123 4568"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Billing Email</Label>
                      <Input
                        type="email"
                        value={branch.billing_email || ''}
                        onChange={(e) => updateBranch(index, 'billing_email', e.target.value)}
                        placeholder="accounts@company.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Billing Contact</Label>
                      <Input
                        value={branch.billing_contact || ''}
                        onChange={(e) => updateBranch(index, 'billing_contact', e.target.value)}
                        placeholder="Contact person name"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-medium text-slate-600 mb-2 block">Physical Address</Label>
                      <AddressFields
                        address={branch.physical_address}
                        onChange={(field, value) => updateBranch(index, field, value)}
                        prefix="physical_address"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-600 mb-2 block">Postal Address</Label>
                      <AddressFields
                        address={branch.postal_address}
                        onChange={(field, value) => updateBranch(index, field, value)}
                        prefix="postal_address"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-sm font-semibold">Contact Persons</h4>
              <p className="text-xs text-slate-500">Add key contacts for this client</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addContact}>
              <Plus className="h-3 w-3 mr-1" />
              Add Contact
            </Button>
          </div>

          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
            {form.contacts.map((contact, index) => (
              <div key={index} className="border rounded-lg p-4 bg-slate-50">
                <div className="flex justify-between items-start mb-3">
                  <Select
                    value={contact.contact_type}
                    onValueChange={(value) => updateContact(index, 'contact_type', value)}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {contactTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.contacts.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeContact(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Full Name</Label>
                    <Input
                      value={contact.name}
                      onChange={(e) => updateContact(index, 'name', e.target.value)}
                      placeholder="John Smith"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Position</Label>
                    <Input
                      value={contact.position || ''}
                      onChange={(e) => updateContact(index, 'position', e.target.value)}
                      placeholder="Financial Director"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input
                      type="email"
                      value={contact.email || ''}
                      onChange={(e) => updateContact(index, 'email', e.target.value)}
                      placeholder="john@company.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Office Phone</Label>
                    <Input
                      value={contact.phone || ''}
                      onChange={(e) => updateContact(index, 'phone', e.target.value)}
                      placeholder="+27 11 123 4567"
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Cell Phone</Label>
                    <Input
                      value={contact.cell || ''}
                      onChange={(e) => updateContact(index, 'cell', e.target.value)}
                      placeholder="+27 82 123 4567"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Certifications Tab */}
        <TabsContent value="certifications" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-6">
            {/* CIDB Section */}
            <div className="border rounded-lg p-4">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Award className="h-4 w-4" />
                CIDB Grading
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">CIDB Grade (1-9)</Label>
                  <Select
                    value={form.certification?.cidb_grade?.toString() || ''}
                    onValueChange={(value) => setForm(prev => ({
                      ...prev,
                      certification: { ...prev.certification, cidb_grade: parseInt(value) }
                    }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(grade => (
                        <SelectItem key={grade} value={grade.toString()}>
                          Grade {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Certificate Number</Label>
                  <Input
                    value={form.certification?.cidb_certificate_number || ''}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      certification: { ...prev.certification, cidb_certificate_number: e.target.value }
                    }))}
                    placeholder="CIDB certificate number"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Expiry Date</Label>
                  <Input
                    type="date"
                    value={form.certification?.cidb_expiry_date || ''}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      certification: { ...prev.certification, cidb_expiry_date: e.target.value }
                    }))}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* BEE Section */}
            <div className="border rounded-lg p-4">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Award className="h-4 w-4" />
                BEE Status
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">BEE Level (1-9)</Label>
                  <Select
                    value={form.certification?.bee_level?.toString() || ''}
                    onValueChange={(value) => setForm(prev => ({
                      ...prev,
                      certification: { ...prev.certification, bee_level: parseInt(value) }
                    }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(level => (
                        <SelectItem key={level} value={level.toString()}>
                          Level {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Certificate Number</Label>
                  <Input
                    value={form.certification?.bee_certificate_number || ''}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      certification: { ...prev.certification, bee_certificate_number: e.target.value }
                    }))}
                    placeholder="BEE certificate number"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Expiry Date</Label>
                  <Input
                    type="date"
                    value={form.certification?.bee_expiry_date || ''}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      certification: { ...prev.certification, bee_expiry_date: e.target.value }
                    }))}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Status Tab */}
        <TabsContent value="status" className="space-y-4 mt-4">
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-sm font-semibold">Account Status</h4>
                <p className="text-xs text-slate-500">
                  Deactivate clients with outstanding payments or other issues
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                />
                <span className={`text-sm font-medium ${form.is_active ? 'text-green-600' : 'text-red-600'}`}>
                  {form.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {!form.is_active && (
              <div>
                <Label className="text-xs text-slate-500">Reason for Deactivation</Label>
                <Textarea
                  value={form.deactivation_reason}
                  onChange={(e) => setForm({ ...form, deactivation_reason: e.target.value })}
                  placeholder="e.g., Outstanding payments, Contract ended..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            )}
          </div>

          <div className="border rounded-lg p-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Additional notes about this client..."
              className="mt-2"
              rows={4}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Form Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800">
          {saving ? 'Saving...' : (client ? 'Update Client' : 'Create Client')}
        </Button>
      </div>
    </form>
  );
}
